// Хук счётчика непрочитанных сообщений.
// Источники:
//   1. С сервера через GET /api/messages — реальные сообщения от админа
//      + авто-уведомления о действиях (выдача, бан, заморозка).
//   2. __GIT_LOG__ как «технический changelog от разработки» — те же
//      коммиты что были раньше. Считаем количество новых с прошлого открытия
//      модалки (через localStorage hash).
//
// Бейдж = сервер unread + git unread.

import { useState, useEffect, useCallback } from 'react';
import { getMessages, getToken, markMessagesRead, getDevChangelog,
  type ServerMessage, type ChangelogCommit } from '../api/client';

const LS_GIT_KEY = 'corps.lastReadCommitHash';
const LS_HIDDEN_KEY = 'corps.hiddenCommitHashes';

// Локально скрытые коммиты (юзер нажал ×). Хранится в localStorage чтобы
// переживало перезагрузки.
function getHiddenHashes(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_HIDDEN_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch { return new Set(); }
}
function saveHiddenHashes(s: Set<string>) {
  try { localStorage.setItem(LS_HIDDEN_KEY, JSON.stringify([...s])); } catch {}
}

function calcGitUnread(hashes: string[]): number {
  if (hashes.length === 0) return 0;
  const lastRead = localStorage.getItem(LS_GIT_KEY);
  if (!lastRead) {
    localStorage.setItem(LS_GIT_KEY, hashes[0]);
    return 0;
  }
  const idx = hashes.indexOf(lastRead);
  if (idx < 0) return Math.min(hashes.length, 9);
  return idx;
}

function markGitRead(hashes: string[]) {
  if (hashes.length > 0) localStorage.setItem(LS_GIT_KEY, hashes[0]);
}

export function useUnreadMessages() {
  const [serverMessages, setServerMessages] = useState<ServerMessage[]>([]);
  const [serverUnread, setServerUnread] = useState(0);
  // МСГ: список коммитов теперь fetch'ится с /api/dev-changelog
  // (vite __GIT_LOG__ на Cloudflare Pages урезается до 1 коммита из-за
  // shallow-clone).
  const [commits, setCommits] = useState<ChangelogCommit[]>([]);
  const [hiddenHashes, setHiddenHashes] = useState<Set<string>>(() => getHiddenHashes());
  const [gitUnread, setGitUnread] = useState(0);

  const refresh = useCallback(async () => {
    // Серверный changelog (без авторизации — публичный эндпоинт).
    try {
      const r = await getDevChangelog(50);
      setCommits(r.items);
      const visibleHashes = r.items
        .filter(c => !hiddenHashes.has(c.hash))
        .map(c => c.hash);
      setGitUnread(calcGitUnread(visibleHashes));
    } catch (e) {
      console.warn('[messages] changelog fetch failed', e);
    }

    if (!getToken()) {
      setServerMessages([]);
      setServerUnread(0);
      return;
    }
    try {
      const resp = await getMessages();
      setServerMessages(resp.messages);
      setServerUnread(resp.unread);
    } catch (e) {
      console.warn('[messages] fetch failed', e);
    }
  }, [hiddenHashes]);

  useEffect(() => { refresh(); }, [refresh]);

  // Этап 63.1 (по идее фаундера): НЕ поллим по таймеру вообще. Счётчик
  // обновляется только по событиям:
  //   (1) заход на главный экран — refresh() в useEffect на монтировании выше
  //       (HomeScreen пересоздаётся при возврате на /home);
  //   (2) возврат в Mini App из фона (вкладка снова видима).
  // Так БД (/api/messages) будится только когда игрок реально открыл/вернулся —
  // в простое Neon спит. (Раньше: 10с-поллинг 24/7 → жёг Neon CU.)
  useEffect(() => {
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const visibleHashes = commits.filter(c => !hiddenHashes.has(c.hash)).map(c => c.hash);
    markGitRead(visibleHashes);
    setGitUnread(0);
    if (getToken()) {
      try {
        await markMessagesRead();
      } catch (e) {
        console.warn('[messages] mark-read failed', e);
      }
    }
    setServerUnread(0);
    setServerMessages(prev => prev.map(m => ({ ...m, readAt: m.readAt || new Date().toISOString() })));
  }, [commits, hiddenHashes]);

  // МСГ: скрыть конкретный коммит (× в модалке). Локально, без сервера.
  const hideCommit = useCallback((hash: string) => {
    setHiddenHashes(prev => {
      const next = new Set(prev);
      next.add(hash);
      saveHiddenHashes(next);
      return next;
    });
  }, []);

  const hideAllCommits = useCallback(() => {
    setHiddenHashes(prev => {
      const next = new Set(prev);
      for (const c of commits) next.add(c.hash);
      saveHiddenHashes(next);
      return next;
    });
  }, [commits]);

  const visibleCommits = commits.filter(c => !hiddenHashes.has(c.hash));

  return {
    unread: serverUnread + gitUnread,
    serverMessages,
    commits: visibleCommits,
    hideCommit,
    hideAllCommits,
    markAllRead,
    refresh
  };
}
